import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation, useSubscription } from 'hooks/apollo';
import { SAVE_FILTERS_CHECKLIST } from '../../mutations';
import { CHECKLIST_FILTER_UPDATED } from '../../subscriptions';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import { useChangeTrackingLens, set, get } from '@totalsoft/change-tracking-react';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { Card } from '@totalsoft/rocket-ui';
import FlexBox from 'components/common/FlexBox';

const actionsSlotId = 'checklist-filters-actions-slot';

const ChecklistFiltersContainer = ({ condition, filters, docTypeAreaId }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Side effects

  useEffect(() => {
    resetLens({
      withTemplate: false,
      templateId: null,
      template: {
        phrase: condition,
        conditions: filters.map(filter => ({
          id: filter.filterId,
          orderNo: filter.orderNo,
          paramId: filter.paramId,
          operatorId: filter.operatorId,
          value: filter.filterValue,
          valueId: filter.filterValueId,
          values: (filter.filterValues || []).map(value => ({
            id: value.valueId,
            detailId: value.filterId,
            value: value.filterValue,
            valueId: value.filterValueId
          }))
        }))
      }
    });
    setInitialData({
      withTemplate: false,
      templateId: null,
      template: {
        phrase: condition,
        conditions: filters.map(filter => ({
          id: filter.filterId,
          orderNo: filter.orderNo,
          paramId: filter.paramId,
          operatorId: filter.operatorId,
          value: filter.filterValue,
          valueId: filter.filterValueId,
          values: (filter.filterValues || []).map(value => ({
            id: value.valueId,
            detailId: value.filterId,
            value: value.filterValue,
            valueId: value.filterValueId
          }))
        }))
      }
    });
  }, [condition, filters, resetLens]);

  // #endregion

  // #region Queries

  const { parameters, operators, loadValues, loadPagedValues } = useGenericFilterData({
    context: GenericFilterContext.RequiredDocument
  });

  // #endregion

  // #region Mutations

  const [saveDetails] = useMutation(SAVE_FILTERS_CHECKLIST, {
    onError: () => {
      (lens.disabled |> set)(false);
    }
  });

  // #endregion

  // #region Subscriptions

  useSubscription(CHECKLIST_FILTER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      client.cache.evict({ id: `ChecklistItem:${data.checklistTypeFiltersUpdated.requiredDocTypeId}` });
      addToast(t('GenericFilterTemplate.Saved'), 'success');
    }
  });

  // #endregion

  // #region Handlers

  const onHandleSave = data => {
    (lens.disabled |> set)(true);
    saveDetails({ variables: { input: { docTypeAreaId, ...data.template } } });
  };

  // #endregion

  return (
    <Card
      icon={ViewQuiltIcon}
      iconColor='primary'
      title={t('EntityFilter.Filters')}
      actions={<FlexBox gap={0.5} alignItems='center' id={actionsSlotId}></FlexBox>}
    >
      <GenericFilterComposer
        lens={lens}
        dirtyInfo={dirtyInfo}
        resetLens={resetLens}
        parameters={parameters}
        operators={operators}
        onSave={onHandleSave}
        loadValues={loadValues}
        loadPagedValues={loadPagedValues}
        withTemplateGeneralData
        actionsSlotId={actionsSlotId}
      />
    </Card>
  );
};

ChecklistFiltersContainer.propTypes = {
  condition: PropTypes.string.isRequired,
  filters: PropTypes.array.isRequired,
  docTypeAreaId: PropTypes.number
};

export default ChecklistFiltersContainer;
